import { LitElement, html, css } from 'lit';
import { router } from './lib/router.js';
import { applySiteMeta, fetchPages, fetchSiteSettings } from './lib/api.js';
import { store } from './lib/store.js';
const PAGE_PATHS = __SITE_CONFIG__.routes || {};

import './components/site-header.js';
import './components/site-footer.js';
import './components/site-draft-panel.js';
import './pages/site-cms-page.js';

// Register routes
for (const [slug, paths] of Object.entries(PAGE_PATHS)) {
  router.route(slug, 'site-cms-page', paths);
}

class SiteApp extends LitElement {
  static properties = {
    _currentSlug: { state: true },
    _isDraft: { state: true },
    _navLoaded: { state: true },
    _draftPageTitle: { state: true },
    _draftPageId: { state: true },
    _draftPageChanged: { state: true },
    _draftPageReady: { state: true },
    _draftSettingsChanged: { state: true },
  };

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
    }
    .main {
      flex: 1;
      padding-top: var(--header-height, 4rem);
    }
    .main--flush {
      padding-top: 0;
    }
    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 60vh;
      flex-direction: column;
      gap: var(--space-4, 1rem);
    }
    .spinner {
      width: 40px;
      height: 40px;
      border: 3px solid var(--color-border, rgba(255,255,255,0.08));
      border-top-color: var(--color-accent, #00d4ff);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    .loading-text {
      font-size: var(--text-sm, 0.875rem);
      color: var(--color-text-muted, #6b6b80);
    }
  `;

  constructor() {
    super();
    this._currentSlug = null;
    this._isDraft = false;
    this._navLoaded = false;
    this._draftPageTitle = 'Home';
    this._draftPageId = null;
    this._draftPageChanged = false;
    this._draftPageReady = false;
    this._draftSettingsChanged = false;

    router.addEventListener('route-changed', (e) => {
      this._currentSlug = e.detail.slug;
      this._isDraft = e.detail.isDraft;
      this._draftPageTitle = e.detail.slug || 'home';
      this._draftPageChanged = false;
      this._draftPageReady = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  async connectedCallback() {
    super.connectedCallback();
    try {
      const [pages, settings] = await Promise.all([fetchPages(), fetchSiteSettings()]);
      for (const page of pages) {
        router.route(page.slug, 'site-cms-page', PAGE_PATHS[page.slug] || [`/${page.slug}/`]);
      }
      store.setNavPages(pages);
      store.setSiteSettings(settings);
      this._draftSettingsChanged = Boolean(settings?.has_unpublished_changes);
    } catch {
      const fallbackPages = (__SITE_FALLBACK__.pages || []).map(({ content, ...page }) => page);
      store.setNavPages(fallbackPages);
      store.setSiteSettings(__SITE_SETTINGS_FALLBACK__);
      applySiteMeta(__SITE_SETTINGS_FALLBACK__);
    }
    this._navLoaded = true;
    router.refresh();
  }

  _handleDraftPageLoaded(event) {
    const detail = event.detail || {};
    if (detail.slug !== (this._currentSlug || 'home')) return;
    this._draftPageTitle = detail.title || detail.slug || 'Page';
    this._draftPageId = detail.id || null;
    this._draftPageChanged = Boolean(detail.hasUnpublishedChanges);
    this._draftPageReady = Boolean(detail.ready);
  }

  _handleDraftPublished(event) {
    if (event.detail?.target === 'site') this._draftSettingsChanged = false;
    if (event.detail?.target === 'page') this._draftPageChanged = false;
  }

  render() {
    if (!this._navLoaded) {
      return html`
        <site-header></site-header>
        <div class="main ${this._currentSlug === 'signup' || this._currentSlug === 'home' ? 'main--flush' : ''}">
          <div class="loading">
            <div class="spinner"></div>
            <div class="loading-text">${store.getSiteSettings()?.interface?.loading_text || 'Loading'}</div>
          </div>
        </div>
        <site-footer></site-footer>
      `;
    }

    return html`
      <site-header></site-header>
      <div class="main ${this._currentSlug === 'signup' || this._currentSlug === 'home' ? 'main--flush' : ''}">
        <site-cms-page
          .slug=${this._currentSlug || 'home'}
          .pageId=${this._draftPageId}
          .draftMode=${this._isDraft}
          @draft-page-loaded=${this._handleDraftPageLoaded}
        ></site-cms-page>
      </div>
      <site-footer></site-footer>
      ${this._isDraft ? html`
        <site-draft-panel
          .slug=${this._currentSlug || 'home'}
          .pageTitle=${this._draftPageTitle}
          .pageChanged=${this._draftPageChanged}
          .pageReady=${this._draftPageReady}
          .settingsChanged=${this._draftSettingsChanged}
          @draft-published=${this._handleDraftPublished}
        ></site-draft-panel>
      ` : ''}
    `;
  }
}

customElements.define('site-app', SiteApp);
